import React from "react";
import {
  Box,
  Grid,
  IconButton,
  Link,
  List,
  ListItem,
  Paper,
  Tooltip,
  Typography,
} from "@mui/material";

import { GetWeblabByNameInput, WeblabRowType } from "../models/API";
import "flag-icon-css/css/flag-icons.min.css";
import { ContentCopyRounded } from "@mui/icons-material";

import { getWeblabByName } from "../graphql/queries";
import { generateClient } from "aws-amplify/api";
import { currencyFormatter } from "./utils/formatters";
import { StatusIcon } from "./utils/StatusIcon";
import { styled } from "@mui/material/styles";
import { buildPhoneToolLinks } from "./utils/Link";
import MarketplaceTile from "./common/MarketplaceTile";
import MetricsTile from "./common/MetricsTile";
import { buildMetricsArray, getMetricsData } from "./common/Metrics";
interface DetailsPanelProps {
  isOpen: boolean;
  weblabId: string | null;
  treatment: string | null;
  selectedGroupRowData: WeblabRowType | null;
  selectedGroupChildrenRows: any[] | null;
}
const StyledGrid = styled(Grid)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "start",
  //padding: theme.spacing(1),
  "&:not(:last-child)": {
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
}));

const DetailsPanelWeblabTreatment: React.FC<DetailsPanelProps> = ({
  isOpen,
  weblabId,
  treatment,
  selectedGroupRowData,
  selectedGroupChildrenRows,
}) => {
  const [description, setDescription] = React.useState("");
  const [deepDiveLink, setDeepDiveLink] = React.useState("");
  const [globalInitName, setGlobalInitName] = React.useState("");
  const client = generateClient();

  // @ts-ignore
  async function getWeblab(weblabId: string) {
    try {
      const input: GetWeblabByNameInput = {
        weblab_name: weblabId,
      };
      const result = await client.graphql({
        query: getWeblabByName,
        variables: {
          input: input,
        },
      });
      const weblab = result.data.getWeblabByName;
      if (weblab != null) {
        setDescription(weblab.description || "");
        setGlobalInitName(weblab.global_init_name || "");
        setDeepDiveLink(weblab.deep_dive_link || "");
        //setOwners(weblab.owners || "");
      }
    } catch (err) {
      console.error(err);
    }
  }

  function buildSection1(children_data: any[]) {
    // basic info are same across all weblabs for a given weblab ID
    let data = null;
    if (treatment) {
      data = children_data[0].data;
    } else {
      data = children_data[0].childrenAfterFilter[0].data;
    }
    let items: Array<any> = [];
    items.push({
      label: "Sub Team",
      value: data.weblab_team,
    });
    items.push({
      label: "Owners",
      value: data.owners,
    });
    items.push({
      label: "Experiment Type",
      value: data.experiment_type,
    });
    items.push({
      label: "Page Type",
      value: data.page_type,
    });
    return items;
  }

  function buildSection2(children_data: any[]) {
    const marketplaces = new Map();
    children_data.forEach((weblab: any) => {
      if (marketplaces.has(weblab.data.marketplace)) {
        const count = marketplaces.get(weblab.data.marketplace);
        marketplaces.set(weblab.data.marketplace, count + 1);
      } else {
        marketplaces.set(weblab.data.marketplace, 1);
      }
    });
    return marketplaces;
  }

  function buildMetrics(children_data: any[]) {
    let data = {
      net_cp_impact: null,
      net_cp_impact_expected: null,
      net_cp_impact_expected_lift: null,
      net_cp_lift: null,
      net_cp_prob: null,
      ops_impact: null,
      ops_impact_expected: null,
      ops_impact_expected_lift: null,
      ops_lift: null,
      ops_prob: null,
      gccp_impact: null,
      gccp_impact_expected: null,
      gccp_impact_expected_lift: null,
      gccp_lift: null,
      gccp_prob: null,
      csales_impact: null,
      csales_impact_expected: null,
      csales_impact_expected_lift: null,
      csales_lift: null,
      csales_prob: null,
      paid_units_impact: null,
      paid_units_impact_expected: null,
      paid_units_impact_expected_lift: null,
      paid_units_lift: null,
      paid_units_prob: null,
      sb_revenue_impact: null,
      sb_revenue_impact_expected: null,
      sb_revenue_impact_expected_lift: null,
      sb_revenue_lift: null,
      sb_revenue_prob: null,
      sp_clicks_impact: null,
      sp_clicks_impact_expected: null,
      sp_clicks_impact_expected_lift: null,
      sp_clicks_lift: null,
      sp_clicks_prob: null,
      sp_revenue_impact: null,
      sp_revenue_impact_expected: null,
      sp_revenue_impact_expected_lift: null,
      sp_revenue_lift: null,
      sp_revenue_prob: null,
      sb_clicks_impact: null,
      sb_clicks_impact_expected: null,
      sb_clicks_impact_expected_lift: null,
      sb_clicks_lift: null,
      sb_clicks_prob: null,
      sp_attributed_ops_1day_expected: null,
      sp_roas_1day_value: null,
    };
    children_data.forEach((weblab: any) => {
      getMetricsData(weblab, data);
    });
    let metrics: Array<any> = [];
    buildMetricsArray(metrics, data);

    return metrics;
  }

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch (err) {
      console.error(err);
    }
  };
  if (!isOpen) {
    return null;
  }

  if (selectedGroupRowData) {
    const section1: Array<any> = buildSection1(selectedGroupChildrenRows!);
    const marketplaces = buildSection2(selectedGroupChildrenRows!);
    const metrics: Array<any> = buildMetrics(selectedGroupChildrenRows!);
    getWeblab(weblabId!);

    // @ts-ignore
    // @ts-ignore
    return (
      <Box p={2}>
        <div style={{ display: "flex", alignItems: "center" }}>
          <a
            href={"https://weblab.amazon.com/wl/" + weblabId}
            target={"_blank"}
            style={{ textDecoration: "none", color: "blue" }}
            rel="noreferrer"
          >
            <Typography
              variant="h5"
              gutterBottom
              style={{ textDecoration: "underline" }}
            >
              {weblabId}
            </Typography>
          </a>
          <Tooltip title={"Copy Weblab ID"}>
            <IconButton
              onClick={() => copyToClipboard(weblabId!)}
              size={"small"}
            >
              <ContentCopyRounded fontSize={"small"} />
            </IconButton>
          </Tooltip>
        </div>
        <Typography variant="subtitle1" gutterBottom>
          {globalInitName ? globalInitName + " | " : ""}
          {deepDiveLink ? (
            <b>
              <Link
                href={deepDiveLink}
                target={"_blank"}
                rel={"noopener noreferrer"}
              >
                Deep Dive Link
              </Link>
              <b>&nbsp;&nbsp;|&nbsp;&nbsp;</b>
            </b>
          ) : (
            ""
          )}
          <b>
            <Link
              href={
                "https://w.amazon.com/bin/view/Weblab/BarRaiser/" + weblabId
              }
              target={"_blank"}
              rel={"noopener noreferrer"}
            >
              Bar Raiser Wiki
            </Link>
          </b>
        </Typography>
        {treatment && (
          <Typography variant="subtitle2" gutterBottom>
            <b>
              Baseline Treatment:&nbsp;
              <b>{treatment.split(":")[0]}</b>
              &nbsp;&nbsp;|&nbsp;&nbsp;Experiment Treatment:&nbsp;
              <b>{treatment.split(":")[1]}</b>
            </b>
          </Typography>
        )}
        <Typography variant="body1" gutterBottom>
          {description}
        </Typography>
        <Grid container spacing={2} marginTop={2}>
          {[section1].map((section, index) => (
            <Grid item xs={12} sm={4} key={"section" + index}>
              <Paper elevation={3} sx={{ padding: 1, marginBottom: 1 }}>
                <List dense>
                  {section.map((item: any, i: number) => (
                    <ListItem key={i} divider>
                      <StyledGrid container key={index}>
                        <Grid item sm={index === 0 ? 5 : 7}>
                          <Typography fontWeight="fontWeightBold">
                            {item.label}
                          </Typography>
                        </Grid>
                        <Grid item sm={index === 0 ? 7 : 5}>
                          {item.label === "Creator" ||
                          item.label === "Owners" ||
                          item.label === "Last Updated By" ? (
                            buildPhoneToolLinks(item.value)
                          ) : item.label === "Status" ? (
                            <Typography>
                              {StatusIcon(item.value)}
                              {item.value}
                            </Typography>
                          ) : item.label === "Expected Revenue Impact" ? (
                            <Typography>
                              {currencyFormatter(item.value)}
                            </Typography>
                          ) : (
                            <Typography>{item.value}</Typography>
                          )}
                        </Grid>
                      </StyledGrid>
                    </ListItem>
                  ))}
                </List>
              </Paper>
              <MarketplaceTile marketplaces={marketplaces} />
            </Grid>
          ))}
          <MetricsTile metrics={metrics} />
        </Grid>
      </Box>
    );
  } else {
    return <div></div>;
  }
};

export default DetailsPanelWeblabTreatment;
